// iceConfig.js - Smart ICE server configuration for local and internet connectivity

/**
 * Returns ICE server configuration that works both online and offline.
 * 
 * - When online: Uses STUN servers for NAT traversal and internet connectivity
 * - When offline/local: Uses empty config to rely on local network discovery (mDNS, host candidates)
 * - Gracefully handles STUN server failures
 */
export const getICEServers = () => {
  // For local network connections, WebRTC can work without STUN servers
  // by using mDNS (.local addresses) and host candidates
  
  // Always include STUN servers but don't fail if they're unreachable
  // WebRTC will fall back to host candidates automatically
  return [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun.cloudflare.com:3478' },
    { urls: 'stun:stun.stunprotocol.org:3478' },
  ]
}

/**
 * Creates RTCPeerConnection with optimal configuration for both
 * local network and internet connectivity.
 */
export const createPeerConnection = () => {
  return new RTCPeerConnection({
    iceServers: getICEServers(),
    // Enable both unified-plan and plan-b for compatibility
    sdpSemantics: 'unified-plan',
    // Important: Allow gathering of all candidate types
    iceTransportPolicy: 'all', // 'all' allows both STUN and local candidates
    // Bundle policy to optimize connection
    bundlePolicy: 'max-bundle',
    // RTCP mux policy
    rtcpMuxPolicy: 'require',
  })
}

/**
 * Enhanced ICE gathering with timeout and local network support
 */
export const waitForICEGathering = (pc, timeoutMs = 10000) => {
  return new Promise((resolve) => {
    if (pc.iceGatheringState === 'complete') {
      console.log('[ICE] Already complete')
      resolve()
      return
    }

    let done = false
    const finish = (reason) => {
      if (done) return
      done = true
      console.log(`[ICE] Gathering finished: ${reason}`)
      clearTimeout(timer)
      pc.removeEventListener('icegatheringstatechange', onState)
      pc.removeEventListener('icecandidate', onCandidate)
      resolve()
    }

    // Set a timeout, but don't fail - just proceed with what we have
    const timer = setTimeout(() => finish('timeout'), timeoutMs)

    const onState = () => {
      console.log(`[ICE] State changed to: ${pc.iceGatheringState}`)
      if (pc.iceGatheringState === 'complete') {
        finish('complete')
      }
    }

    const onCandidate = (e) => {
      if (e.candidate) {
        console.log(`[ICE] Candidate: ${e.candidate.type} ${e.candidate.address || e.candidate.candidate}`)
      } else {
        console.log('[ICE] All candidates gathered (null candidate)')
        finish('null-candidate')
      }
    }

    pc.addEventListener('icegatheringstatechange', onState)
    pc.addEventListener('icecandidate', onCandidate)
  })
}

/**
 * Log connection diagnostics - useful for debugging local network issues
 */
export const logConnectionDiagnostics = (pc, label = '') => {
  console.group(`[WebRTC Diagnostics] ${label}`)
  console.log('Connection State:', pc.connectionState)
  console.log('ICE Connection State:', pc.iceConnectionState)
  console.log('ICE Gathering State:', pc.iceGatheringState)
  console.log('Signaling State:', pc.signalingState)
  
  pc.getStats().then(stats => {
    stats.forEach(report => {
      if (report.type === 'candidate-pair' && report.state === 'succeeded') {
        console.log('Active Candidate Pair:', report)
      }
      if (report.type === 'local-candidate') {
        console.log('Local Candidate:', {
          type: report.candidateType,
          address: report.address || report.ip,
          port: report.port,
          protocol: report.protocol
        })
      }
    })
  })
  console.groupEnd()
}